import React, {forwardRef} from "react";

interface IDataTableV2Config {
  tableConfig?: {
    stickyHeader?: boolean;
    stickyHeaderTableMaxHeight?: string;
  };
  cachingFilters?: {
    enableCaching: boolean;
    saveCachingIntoServer?: (updatedCachingFilter: any) => void;
    getCachingConfigFromServerAtLoad?: () => any;
  };
}

interface IDataTableV2Context extends IDataTableV2Config {
  cachingDataState: IDataTableV2CachedDataState;
  getCachedDataByKey: (uniqueKey: string) => any | null;
  saveDataInCache: (uniqueKey: string, updatedData: any) => void;
}

const DataTableContext = React.createContext<IDataTableV2Context>({
  tableConfig: {},
  cachingDataState: {},
  getCachedDataByKey: () => {},
  saveDataInCache: () => {},
});

export interface IDataTableV2CachedDataState {
  [key: string]: any;
}

export interface DataTableV2ProviderHandle {
  getCachingConfigFromServerAtLoad?: () => any;
}

export const useDataTableV2Context = () => React.useContext(DataTableContext);

export const DataTableV2Provider=forwardRef<
DataTableV2ProviderHandle,
{ children?: React.ReactNode } & IDataTableV2Config
>((props, ref) => {
  const {
    cachingFilters = { enableCaching: false, ...props.cachingFilters },
    tableConfig,
  } = props;
  const { enableCaching } = cachingFilters;

  const [cachingDataState, setCachingDataState] =
    React.useState<IDataTableV2CachedDataState>({});

  const getCachedDataByKey = (uniqueKey: string): any | null => {
    if (cachingDataState[uniqueKey]) {
      return cachingDataState[uniqueKey];
    }
    return null;
  };

  const saveDataInCache = (uniqueKey: string, updatedData: any) => {
    if (enableCaching) {
      const cachedData = localStorage.getItem("tablePreferences");
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        const finalData = { ...parsedData };
        finalData[uniqueKey] = updatedData;
        setCachingDataState(finalData);
        localStorage.setItem("tablePreferences", JSON.stringify(finalData));
        if (cachingFilters.saveCachingIntoServer) {
          cachingFilters.saveCachingIntoServer(finalData);
        }
      } else {
        const finalData: any = {};
        finalData[uniqueKey] = updatedData;
        setCachingDataState(finalData);
        localStorage.setItem("tablePreferences", JSON.stringify(finalData));
        if (cachingFilters.saveCachingIntoServer) {
          cachingFilters.saveCachingIntoServer(finalData);
        }
      }
    }
  };

  React.useEffect(() => {
    (async () => {
      if (enableCaching) {
       
          const cachedData = localStorage.getItem("tablePreferences");
          if (cachedData) {
            const parsedData = JSON.parse(cachedData);
            setCachingDataState(parsedData);
          }
        
      }
    })();
  }, []);

  React.useImperativeHandle(ref, () => ({
    getCachingConfigFromServerAtLoad: async()=> {
     if( cachingFilters.getCachingConfigFromServerAtLoad){
      const data = await cachingFilters.getCachingConfigFromServerAtLoad();
      localStorage.setItem("tablePreferences", JSON.stringify(data));
      setCachingDataState(data);
     }
    },
  }));

  return (
    <DataTableContext.Provider
      value={{
        cachingDataState: cachingDataState,
        tableConfig: tableConfig,
        getCachedDataByKey: getCachedDataByKey,
        saveDataInCache,
      }}
    >
      {props.children}
    </DataTableContext.Provider>
  );
});
